/**
 * Compute unit (CU) limits per instruction and variant.
 *
 * Each key is an instruction name, and the value is a record of variant names to CU limits.
 * The "default" variant is used when no variant is specified.
 * Validated by SDK compatibility tests.
 */
export const CU_TABLE: Record<string, Record<string, number>> = {
  buy: { default: 470_000, graduation: 400_000 },
  sell: { default: 250_000 },
  // Sell-cap program: creates one trader position PDA before a first capped buy.
  init_position: { default: 30_000 },
  // Graduated cp-swap pool swap (swap_base_input): constant-product math +
  // two vault transfers + oracle observation update.
  amm_swap: { default: 350_000 },
  // Retain the existing creation/preparation budgets while updating instruction names.
  create_market: { default: 900_000 },
  create_timed_market: { default: 900_000 },
  stage_outcome_metadata: { default: 250_000 },
  // One transaction: state activation plus a single deterministic outcome preparation.
  add_outcome: { default: 1_300_000 },
  prepare_outcomes: { default: 1_200_000 },
  claim_prize: { default: 150_000 },
  claim_backer_fees: { default: 150_000 },
  resolve_drain_and_migrate: { default: 470_000 },
  boost_market: { default: 150_000 },
  admin: { default: 100_000 },
};

/**
 * Look up the compute unit limit for an instruction/variant.
 *
 * Returns the CU limit for the given variant, falling back to "default" if no variant
 * is specified, or 200_000 if the instruction is not in the table.
 *
 * @param instruction - Instruction name (e.g. "buy", "sell", "resolve")
 * @param variant - Optional variant (e.g. "graduation"); defaults to "default"
 * @returns Compute unit limit
 */
export function estimateComputeUnits(instruction: string, variant?: string): number {
  const entry = CU_TABLE[instruction];
  if (!entry) {
    return 200_000;
  }
  const key = variant ?? 'default';
  return entry[key] ?? entry.default ?? 200_000;
}
